import { createContext, ReactNode, useContext, useState } from 'react'

type Disclosure = {
  openId: number | null
  setOpenId: (id: number | null) => void
}

const DisclosureContext = createContext<Disclosure | null>(null)

/**
 * Shares which row's unlock prompt is open across a player list, so opening
 * one prompt closes the others. Rows outside a provider fall back to local
 * state (mirrors mobile's ProfileUnlockDisclosureProvider).
 */
export const ProfileUnlockDisclosureProvider = ({ children }: { children: ReactNode }) => {
  const [openId, setOpenId] = useState<number | null>(null)
  return <DisclosureContext.Provider value={{ openId, setOpenId }}>{children}</DisclosureContext.Provider>
}

/**
 * Prompt open/close state for one row, shared through the disclosure provider
 * when present, local otherwise.
 */
export function usePromptDisclosure(playerId: number | undefined) {
  const disclosure = useContext(DisclosureContext)
  const [localOpen, setLocalOpen] = useState(false)

  if (disclosure) {
    const promptOpen = playerId != null && disclosure.openId === playerId
    return {
      promptOpen,
      openPrompt: () => disclosure.setOpenId(playerId ?? null),
      closePrompt: () => disclosure.setOpenId(null),
    }
  }

  return {
    promptOpen: localOpen,
    openPrompt: () => setLocalOpen(true),
    closePrompt: () => setLocalOpen(false),
  }
}
